import type { CSSProperties } from "react";

import type { OverlayGeometry, OverlayPositionStyle } from "./editorRelativeRect.ts";

const baseSvgStyle: CSSProperties = {
  pointerEvents: "none",
  overflow: "visible",
  fill: "none",
};

type OverlayBorderProps = {
  geometry: OverlayGeometry;
  /** Override `geometry.overlayStyle` (e.g. resize preview). Defaults to `geometry.overlayStyle`. */
  overlayStyle?: OverlayPositionStyle;
  /** Final on-screen stroke width in device pixels. Counter-scaled by canvas zoom internally. */
  strokeWidth: number;
  dashed?: boolean;
  /** Render a sharp rectangle ignoring the element's border-radius. */
  ignoreBorderRadius?: boolean;
  className?: string;
};

/**
 * Renders an SVG `<rect>` aligned with a transformed canvas element. Stroke
 * width and dash lengths are counter-scaled by canvas zoom so they stay a
 * constant device-pixel size. Style colour/z-index/opacity by wrapping in
 * `styled(OverlayBorder)` and writing CSS against the inner `rect`.
 */
export const OverlayBorder = ({
  geometry,
  overlayStyle,
  strokeWidth,
  dashed,
  ignoreBorderRadius,
  className,
}: OverlayBorderProps) => {
  const scale = geometry.scaleX;
  const rectStyle: CSSProperties = {
    strokeWidth: strokeWidth / scale,
    strokeDasharray: dashed ? `${4 / scale} ${4 / scale}` : undefined,
  };
  return (
    <svg className={className} style={{ ...baseSvgStyle, ...(overlayStyle ?? geometry.overlayStyle) }}>
      <rect
        width="100%"
        height="100%"
        rx={ignoreBorderRadius ? 0 : geometry.borderRadius.rx}
        ry={ignoreBorderRadius ? 0 : geometry.borderRadius.ry}
        fill="none"
        style={rectStyle}
      />
    </svg>
  );
};
